import { useState } from 'react';
import { useApprovalScope } from './useApprovalScope';
import { useCheckFundingCodeMutation } from './useCheckFundingCodeMutation';
import { readInvoiceRejection } from '../services/invoiceRejection';
import { fundingSelectionKey, isCompleteCode } from '../utils/fundingCode';
import { codeCheckFailure, codeCheckKey } from '../utils/fundingCodeCheck';
import type { CodeCheckFailure, CodeCheckStatus, CodeCheckView } from '../utils/fundingCodeCheck';
import type { FundingPurpose, InvoiceLinePayload, RejectionRecorder } from '../types/agreements.types';

interface Options {
    agreementId: string | undefined;
    purpose: FundingPurpose;
    approvalId: string | null;
    code: string;
    lines: InvoiceLinePayload[];
    beginRejection?: () => RejectionRecorder;
}

export interface FundingCodeCheck extends CodeCheckView {
    check: () => Promise<void>;
    reset: () => void;
}

interface CheckState {
    token: number;
    status: Exclude<CodeCheckStatus, 'idle'>;
    failure: CodeCheckFailure | null;
}

/** Any input change or reset starts a new scope, so no earlier result or request can count again.
 *  Component state only, never persisted or logged. */
export function useFundingCodeCheck({ agreementId, purpose, approvalId, code, lines, beginRejection }: Options): FundingCodeCheck {
    const key = codeCheckKey({ agreementId, purpose, approvalId, code, selectionKey: fundingSelectionKey(lines) });
    const scope = useApprovalScope(key);
    const [state, setState] = useState<CheckState | null>(null);
    const mutation = useCheckFundingCodeMutation(agreementId);
    const current = state?.token === scope.token ? state : null;

    const check = async (): Promise<void> => {
        const token = scope.token;
        if (!agreementId || !approvalId || !isCompleteCode(code) || !scope.lock.acquire(token)) return;
        const land = (next: Omit<CheckState, 'token'>) => {
            if (scope.isCurrent(token)) setState((previous) => (previous?.token === token ? { token, ...next } : previous));
        };
        const recordRejection = beginRejection?.();
        setState({ token, status: 'checking', failure: null });
        try {
            await mutation.mutateAsync({ approvalId, purpose, lines, code });
            land({ status: 'accepted', failure: null });
        } catch (error) {
            const reasons = readInvoiceRejection(error);
            recordRejection?.(agreementId, reasons);
            land({ status: 'rejected', failure: codeCheckFailure(reasons, lines) });
        } finally {
            scope.lock.release(token);
        }
    };

    return {
        status: current?.status ?? 'idle',
        fieldMessage: current?.failure?.fieldMessage ?? null,
        overBalanceMessage: current?.failure?.overBalanceMessage ?? null,
        check,
        reset: scope.reset,
    };
}
